import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useHousehold } from '../context/HouseholdContext';
import { useTheme } from '../context/ThemeContext';
import Logo from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEP = {
  PHONE: 'phone',
  CODE: 'code',
  PROFILE: 'profile',
  ATHLETE: 'athlete',
  DONE: 'done',
};

/**
 * One-screen, multi-step onboarding.
 *
 * 1. Enter phone -> request OTP
 * 2. Enter 6-digit code -> verify, mint partial JWT
 * 3. Pick role (Parent / Athlete / Coach / Fan) + name + optional email
 *    -> /onboarding/complete creates Household
 * 4. (Parents / Athletes only) Add the first athlete with optional
 *    CricClubs / CricHeroes / str8bat IDs
 * 5. Route to dashboard
 *
 * The whole flow is keyboard-driven, mobile-first, and shows a debug
 * OTP banner in dev mode for self-test without an SMS provider.
 */
export default function OnboardingFlow() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { requestOtp, verifyOtp, completeOnboarding, isAuthenticated, onboardingComplete } = useAuth();
  const { addAthlete } = useHousehold();

  const [step, setStep] = useState(STEP.PHONE);
  const [phone, setPhone] = useState('+1');
  const [code, setCode] = useState('');
  const [debugCode, setDebugCode] = useState(null);
  const [busy, setBusy] = useState(false);

  const [role, setRole] = useState('parent');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [householdName, setHouseholdName] = useState('');

  const [athlete, setAthlete] = useState({
    full_name: '',
    primary_role: 'Batter',
    batting_hand: 'right',
    bowling_hand: 'right',
    club_name: '',
    state: '',
    cricclubs_id: '',
    cricheroes_id: '',
    str8bat_id: '',
  });

  useEffect(() => {
    if (isAuthenticated && onboardingComplete) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, onboardingComplete, navigate]);

  // --- step 1: request OTP ---
  const submitPhone = async (e) => {
    e?.preventDefault();
    setBusy(true);
    const res = await requestOtp(phone.trim());
    setBusy(false);
    if (!res.success) return toast.error(res.error || 'Could not send code');
    if (res.debug_code) setDebugCode(res.debug_code);
    toast.success('Code sent to your phone');
    setStep(STEP.CODE);
  };

  // --- step 2: verify ---
  const submitCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    const res = await verifyOtp(phone.trim(), code.trim());
    setBusy(false);
    if (!res.success) return toast.error(res.error || 'Wrong code');
    if (res.onboarding_complete) {
      navigate('/dashboard', { replace: true });
    } else {
      setStep(STEP.PROFILE);
    }
  };

  // --- step 3: profile + household ---
  const submitProfile = async (e) => {
    e?.preventDefault();
    if (!fullName.trim()) return toast.error('Please enter your name');
    setBusy(true);
    const res = await completeOnboarding({
      full_name: fullName.trim(),
      role,
      email: email.trim() || undefined,
      household_name: householdName.trim() || undefined,
    });
    setBusy(false);
    if (!res.success) return toast.error(res.error || 'Could not save');

    if (role === 'parent' || role === 'athlete') {
      // Parents add a kid; athletes confirm their own athlete record.
      setAthlete((a) => ({ ...a, full_name: role === 'athlete' ? fullName : '' }));
      setStep(STEP.ATHLETE);
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  // --- step 4: athlete ---
  const submitAthlete = async (e) => {
    e?.preventDefault();
    if (!athlete.full_name.trim()) return toast.error('Athlete name required');
    setBusy(true);
    try {
      await addAthlete({
        full_name: athlete.full_name.trim(),
        primary_role: athlete.primary_role,
        batting_hand: athlete.batting_hand,
        bowling_hand: athlete.bowling_hand,
        club_name: athlete.club_name.trim() || undefined,
        state: athlete.state.trim() || undefined,
        cricclubs_id: athlete.cricclubs_id.trim() || undefined,
        cricheroes_id: athlete.cricheroes_id.trim() || undefined,
        str8bat_id: athlete.str8bat_id.trim() || undefined,
      });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add athlete');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-10"
      style={{
        background: theme === 'dark'
          ? 'radial-gradient(circle at 20% 0%, #0e2438 0%, #0a0e27 40%, #060818 100%)'
          : 'radial-gradient(circle at 20% 0%, #e9fbf3 0%, #f8fafc 40%, #eef2f7 100%)',
        color: theme === 'dark' ? '#f7fafc' : '#0f172a',
      }}
    >
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo size={44} showTagline />
        </div>

        <div
          className="rounded-3xl p-7"
          style={{
            background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
            border: '1px solid rgba(0,201,167,0.2)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 30px 60px -30px rgba(0,166,81,0.25)',
          }}
        >
          <Stepper step={step} />

          {step === STEP.PHONE && (
            <Form onSubmit={submitPhone} title="Sign in or get started" subtitle="We'll text you a 6-digit code.">
              <Field
                label="Mobile number"
                value={phone}
                onChange={setPhone}
                type="tel"
                autoFocus
                placeholder="+1 415 555 1234"
              />
              <Submit busy={busy} label="Send code" />
              <p className="text-xs opacity-60 mt-4 text-center">
                No password. We use phone codes so it's quick — even for kids' parents.
              </p>
            </Form>
          )}

          {step === STEP.CODE && (
            <Form
              onSubmit={submitCode}
              title="Enter the code"
              subtitle={`Sent to ${phone}. Expires in 10 minutes.`}
            >
              {debugCode && (
                <div
                  className="text-xs px-3 py-2 mb-3 rounded-md text-center"
                  style={{ background: 'rgba(0,201,167,0.1)', color: '#00c9a7' }}
                >
                  DEV mode — your code is <strong>{debugCode}</strong>
                </div>
              )}
              <Field
                label="6-digit code"
                value={code}
                onChange={setCode}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoFocus
                placeholder="123456"
              />
              <Submit busy={busy} label="Verify" />
              <button
                type="button"
                onClick={() => setStep(STEP.PHONE)}
                className="text-xs opacity-60 mt-4 underline w-full text-center"
              >
                Use a different number
              </button>
            </Form>
          )}

          {step === STEP.PROFILE && (
            <Form onSubmit={submitProfile} title="Tell us about you" subtitle="This sets up your family on Cricket Refreshed.">
              <Field label="Your full name" value={fullName} onChange={setFullName} autoFocus />
              <div>
                <label className="block text-sm font-medium opacity-80 mb-2">I am a…</label>
                <RoleGrid value={role} onChange={setRole} />
              </div>
              <Field
                label="Email (optional, for receipts and digests)"
                value={email}
                onChange={setEmail}
                type="email"
                placeholder="you@example.com"
              />
              <Field
                label="Household name (optional)"
                value={householdName}
                onChange={setHouseholdName}
                placeholder="The Kadur Family"
              />
              <Submit busy={busy} label="Continue" />
            </Form>
          )}

          {step === STEP.ATHLETE && (
            <Form
              onSubmit={submitAthlete}
              title={role === 'athlete' ? 'Your athlete profile' : 'Add your athlete'}
              subtitle="You can add more later from settings."
            >
              <Field
                label="Athlete name"
                value={athlete.full_name}
                onChange={(v) => setAthlete({ ...athlete, full_name: v })}
                autoFocus
              />
              <div className="grid grid-cols-2 gap-3">
                <Select
                  label="Primary role"
                  value={athlete.primary_role}
                  onChange={(v) => setAthlete({ ...athlete, primary_role: v })}
                  options={['Batter', 'Bowler', 'All-Rounder', 'Wicket-Keeper']}
                />
                <Select
                  label="Batting hand"
                  value={athlete.batting_hand}
                  onChange={(v) => setAthlete({ ...athlete, batting_hand: v })}
                  options={[['right','Right'], ['left','Left'], ['unknown','Not sure']]}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Club (optional)"
                  value={athlete.club_name}
                  onChange={(v) => setAthlete({ ...athlete, club_name: v })}
                />
                <Field
                  label="State (optional)"
                  value={athlete.state}
                  onChange={(v) => setAthlete({ ...athlete, state: v })}
                  placeholder="CA"
                />
              </div>

              <details className="mt-2">
                <summary className="cursor-pointer text-sm opacity-80">
                  Link existing accounts (CricClubs, CricHeroes, str8bat)
                </summary>
                <div className="mt-3 space-y-3">
                  <Field
                    label="CricClubs player ID"
                    value={athlete.cricclubs_id}
                    onChange={(v) => setAthlete({ ...athlete, cricclubs_id: v })}
                  />
                  <Field
                    label="CricHeroes ID"
                    value={athlete.cricheroes_id}
                    onChange={(v) => setAthlete({ ...athlete, cricheroes_id: v })}
                  />
                  <Field
                    label="str8bat player ID"
                    value={athlete.str8bat_id}
                    onChange={(v) => setAthlete({ ...athlete, str8bat_id: v })}
                  />
                  <p className="text-xs opacity-60">
                    We'll pull match scores and sensor sessions automatically once linked.
                  </p>
                </div>
              </details>

              <Submit busy={busy} label="Finish setup" />
            </Form>
          )}
        </div>

        <div className="text-center text-xs opacity-50 mt-6">
          By continuing you agree to our Terms and Privacy Policy.
        </div>
      </div>
    </div>
  );
}

// ---------- small presentational helpers ----------

function Stepper({ step }) {
  const steps = ['phone', 'code', 'profile', 'athlete'];
  const idx = steps.indexOf(step);
  return (
    <div className="flex gap-1.5 mb-6">
      {steps.map((s, i) => (
        <div
          key={s}
          className="h-1 flex-1 rounded-full transition-all"
          style={{
            background: i <= idx ? 'linear-gradient(90deg, #00A651, #00D9FF)' : 'rgba(150,160,180,0.2)',
          }}
        />
      ))}
    </div>
  );
}

function Form({ title, subtitle, onSubmit, children }) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm opacity-70 mt-1">{subtitle}</p>}
      </div>
      {children}
    </form>
  );
}

function Field({ label, value, onChange, ...rest }) {
  return (
    <div>
      <label className="block text-sm font-medium opacity-80 mb-1.5">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3.5 py-2.5 rounded-lg outline-none transition-all"
        style={{
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(150,160,180,0.25)',
          color: 'inherit',
        }}
        {...rest}
      />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  const normalized = options.map((o) => Array.isArray(o) ? o : [o, o]);
  return (
    <div>
      <label className="block text-sm font-medium opacity-80 mb-1.5">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg"
        style={{
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(150,160,180,0.25)',
          color: 'inherit',
        }}
      >
        {normalized.map(([val, label]) => (
          <option key={val} value={val}>{label}</option>
        ))}
      </select>
    </div>
  );
}

function RoleGrid({ value, onChange }) {
  const opts = [
    { id: 'parent', label: 'Parent', desc: 'Manage my kid' },
    { id: 'athlete', label: 'Athlete', desc: 'It\'s for me' },
    { id: 'coach', label: 'Coach', desc: 'I train players' },
    { id: 'fan', label: 'Fan', desc: 'I just love cricket' },
  ];
  return (
    <div className="grid grid-cols-2 gap-2.5">
      {opts.map((o) => {
        const selected = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className="text-left rounded-xl px-3 py-2.5 transition-all"
            style={{
              background: selected
                ? 'linear-gradient(135deg, rgba(0,166,81,0.15), rgba(0,217,255,0.15))'
                : 'rgba(255,255,255,0.04)',
              border: `1.5px solid ${selected ? '#00C9A7' : 'rgba(150,160,180,0.25)'}`,
            }}
          >
            <div className="font-semibold">{o.label}</div>
            <div className="text-xs opacity-70">{o.desc}</div>
          </button>
        );
      })}
    </div>
  );
}

function Submit({ busy, label }) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="w-full px-4 py-3 rounded-xl font-semibold transition-all"
      style={{
        background: busy
          ? 'rgba(150,160,180,0.3)'
          : 'linear-gradient(135deg, #00A651 0%, #00D9FF 100%)',
        color: '#fff',
        boxShadow: busy ? 'none' : '0 10px 25px -10px rgba(0,201,167,0.6)',
      }}
    >
      {busy ? 'Working…' : label}
    </button>
  );
}
